import { Pressable, Text, View } from 'react-native';
import { getLegalUrl, openExternalUrl } from '../lib/utils';
import { getAiProviderRecipient } from '../lib/ai-provider';
import { Button } from './Button';
import { Card } from './Card';
import { SectionHeader } from './SectionHeader';

type AiDataSharingConsentProps = {
  provider: string | null | undefined;
  onAccept: () => void;
  onDecline?: () => void;
  loading?: boolean;
  error?: string | null;
  ctaLabel?: string;
  title?: string;
};

const PRIVACY_POLICY_URL = getLegalUrl('privacy');

export function AiDataSharingConsent({
  provider,
  onAccept,
  onDecline,
  loading = false,
  error,
  ctaLabel = 'Allow and continue',
  title = 'AI Data Sharing Permission',
}: AiDataSharingConsentProps) {
  const recipient = getAiProviderRecipient(provider);

  return (
    <Card>
      <SectionHeader title={title} />
      <Text className="text-sm text-gray-600 dark:text-gray-300">
        Ask AI uses a third-party AI provider to answer your questions. Per Diem will only send
        data after you approve it.
      </Text>

      <View className="mt-4 gap-2 rounded-2xl bg-gray-50 px-4 py-4 dark:bg-gray-800">
        <Text className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Data that may be sent
        </Text>
        <Text className="text-sm text-gray-700 dark:text-gray-200">
          • Your chat messages and follow-up questions
        </Text>
        <Text className="text-sm text-gray-700 dark:text-gray-200">
          • Relevant balances, transactions, budgets, income, holdings, and FIRE plan outputs
        </Text>
        <Text className="text-sm text-gray-700 dark:text-gray-200">
          • Any extra details you type into Ask AI
        </Text>
      </View>

      <View className="mt-4 rounded-2xl border border-gray-200 px-4 py-4 dark:border-gray-700">
        <Text className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Current provider
        </Text>
        <Text className="mt-1 text-sm font-semibold text-gray-900 dark:text-gray-100">
          {recipient}
        </Text>
        <Text className="mt-2 text-sm text-gray-600 dark:text-gray-300">
          If you switch providers later, Per Diem will ask for permission again before sending data.
        </Text>
      </View>

      <Pressable
        onPress={() => openExternalUrl(PRIVACY_POLICY_URL)}
        className="mt-4 self-start"
      >
        <Text className="text-sm font-medium text-green-600 dark:text-green-400">
          Review privacy policy
        </Text>
      </Pressable>

      {error ? (
        <Text className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</Text>
      ) : null}

      <View className="mt-5 flex-row gap-3">
        <Button className="flex-1" loading={loading} onPress={onAccept}>
          {ctaLabel}
        </Button>
        {onDecline ? (
          <Button className="flex-1" variant="secondary" onPress={onDecline}>
            Not now
          </Button>
        ) : null}
      </View>
    </Card>
  );
}
